import { QueryClient } from '@tanstack/react-query';

/**
 * Status-aware retry policy. Deterministic client errors must never be retried:
 * a 404 ("no employee record for this user") stays a 404 no matter how many
 * times it's asked, and retrying it was hammering /employees/me/* after the
 * auth migration. 401/403 are handled by the axios auth interceptors, not by
 * re-asking. Only rate limiting (429), server errors (5xx) and network
 * failures get a bounded retry.
 */
export function retryByStatus(failureCount: number, error: unknown): boolean {
  const status: number | undefined =
    (error as { response?: { status?: number } })?.response?.status ??
    (error as { status?: number })?.status;
  if (status === 429) return failureCount < 2;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 2; // 5xx or network error
}

// Shared React Query client. Server state (My Pay, etc.) lives here, not Redux.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: retryByStatus,
      refetchOnWindowFocus: false,
    },
  },
});
